import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { getCarrotLite } from "reducers/companyInfo"
import { Spacer } from "../atoms/Atoms"
import HowToUseYourBenefitCard from "./HowToUseYourBenefitCard"
import AvailableFundsModule from "../molecules/AvailableFundsModule"
import { CenteredLoadingIndicator, HomeHeader } from "../molecules/Molecules"
// eslint-disable-next-line import/no-restricted-paths
import { MasterLayout } from "../atoms/layouts/MasterLayout"
import { BodyWithRightSidebarLayout } from "../atoms/layouts/BodyWithRightSidebarLayout"
import { ScrollToTop } from "../ScrollToTop"
import { SideBarBase } from "components/views/atoms/SidebarBase"
import { BenefitEnrollmentModule } from "components/views/molecules/BenefitEnrollmentModule"
import { useToggle } from "utils/Hooks"
import { BenefitEnrollmentFlowContainer } from "components/cmd-enrollment"
import { useBenefitEnrollment } from "../../context/enrollment/BenefitEnrollmentContext"
import { Row, RowItem } from "../atoms/row/Row"
import useCarrotPlan from "../../carrot-plans/hooks/useCarrotPlan"
import { RefreshCarrotPlanModule } from "../molecules/RefreshCarrotPlanModule"
import { SupportCenterModule } from "../molecules/SupportCenterModule"
import ReadAndLearnEligibilityModule from "components/home/eligibility-modules/ReadAndLearn"
import { useCurrentUser } from "../../context/user/UserContext"
import { useHistory, useLocation } from "react-router-dom"
import BenefitsModule from "components/home/benefits-modules"
import { Hero } from "../../home/Hero"
import Locations from "./navigation"
import { CarrotPlansRefreshModule } from "components/home/CarrotPlansRefreshModule"
import { JourneyCompleteNoteModal } from "components/home/JourneyCompleteNoteModal"
import useCarrotPlanEmployeeSettings from "../../carrot-plans/hooks/useCarrotPlanEmployeeSettings"
import EmailVerifiedModal from "../../EmailVerification/EmailVerifiedModal"
import { getSubsidies } from "../../../reducers/subsidies"

// @ts-expect-error TS7031
export const HomeCard = ({ halfWidth, children }): JSX.Element => {
  const width = halfWidth ? "w-50" : "w-100"

  return (
    <div className={`${width} min-w250px`}>
      {children}
      <div className="db dn-m dn-l">
        <Spacer height={3} />
      </div>
      <div className="dn db-ns">
        <Spacer height={2.25} />
      </div>
    </div>
  )
}

HomeCard.propTypes = {
  halfWidth: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired
}
HomeCard.defaultProps = {
  halfWidth: false
}

function EligibilityModules(): JSX.Element {
  return (
    <Row space="24px">
      <RowItem minWidth="239px">
        <HowToUseYourBenefitCard />
      </RowItem>
      <RowItem minWidth="239px">
        <ReadAndLearnEligibilityModule />
      </RowItem>
    </Row>
  )
}

const HomeMain = (): JSX.Element => {
  return (
    <div className="flex flex-row flex-wrap">
      <CarrotPlansRefreshModule />
      <Hero />
      <div className="w-100">
        <Spacer height={3} />
      </div>
      <EligibilityModules />
    </div>
  )
}

const SidebarModules = (): JSX.Element => {
  const { status: benefitEnrollmentStatus, availabilityStatus } = useBenefitEnrollment()
  const { needsBenefitEnrollment } = benefitEnrollmentStatus
  const [showBenefitEnrollmentModal, toggleBenefitEnrollmentModal] = useToggle(false)
  const isCarrotCore = useSelector(getCarrotLite)
  const { data: memberHasACarrotPlan, isLoading } = useCarrotPlan()
  const { isInAppLockdown } = useCurrentUser()
  const subsidies = useSelector(getSubsidies)
  // @ts-expect-error TS7006
  const hasUnlimitedBenefit = subsidies?.some((plan) => plan.hasUnlimitedBenefit)
  const { isDoneForNow, appIsUnlockedAndPendingCarrotPlan } = useCarrotPlanEmployeeSettings()

  const sideModulesToRender = useMemo(() => {
    const modules = []

    if (isLoading) {
      return [<CenteredLoadingIndicator key={"centered-loading-indicator"} id={"loading-indicator-test"} />]
    }

    if (!isCarrotCore && !hasUnlimitedBenefit) {
      modules.push(<BenefitsModule appLockedDown={isInAppLockdown} availabilityStatus={availabilityStatus} />)
    } else {
      if (!isCarrotCore) {
        if (isInAppLockdown && !hasUnlimitedBenefit) {
          modules.push(<AvailableFundsModule appLockedDown={isInAppLockdown} />)
        } else {
          if (needsBenefitEnrollment) {
            modules.push(<BenefitEnrollmentModule onClickEnroll={toggleBenefitEnrollmentModal} />)
          } else if (!hasUnlimitedBenefit) {
            modules.push(<AvailableFundsModule appLockedDown={isInAppLockdown} />)
          }
        }
      }
    }

    if (isCarrotCore) {
      modules.push(<SupportCenterModule />)
    }

    if (memberHasACarrotPlan && !isDoneForNow && !appIsUnlockedAndPendingCarrotPlan) {
      modules.push(<RefreshCarrotPlanModule />)
    }

    return modules
  }, [
    isLoading,
    isCarrotCore,
    hasUnlimitedBenefit,
    memberHasACarrotPlan,
    isDoneForNow,
    appIsUnlockedAndPendingCarrotPlan,
    isInAppLockdown,
    availabilityStatus,
    needsBenefitEnrollment,
    toggleBenefitEnrollmentModal
  ])

  return (
    <>
      {sideModulesToRender.map((module, idx) => (
        <React.Fragment key={idx}>{module}</React.Fragment>
      ))}
      {showBenefitEnrollmentModal && <BenefitEnrollmentFlowContainer onExit={toggleBenefitEnrollmentModal} />}
    </>
  )
}

export const HomeSidebar = (): JSX.Element => {
  return (
    <SideBarBase>
      <SidebarModules />
    </SideBarBase>
  )
}

type LocationState = {
  state: {
    [key: string]: unknown
  }
}

const Home = (): JSX.Element => {
  const location: LocationState = useLocation()
  const state = location.state || {}
  const history = useHistory()

  const onEnrollmentExit = (): void => {
    history.push(Locations.Home.Index)
  }

  return (
    // See https://trello.com/c/qQYnzDnt This was added because on mobile, home would load scrolled down a little.
    <ScrollToTop>
      <MasterLayout header={<HomeHeader />}>
        <BodyWithRightSidebarLayout sidebar={<HomeSidebar />}>
          <HomeMain />
        </BodyWithRightSidebarLayout>
      </MasterLayout>
      {state.showJourneyCompleteNoteModal && <JourneyCompleteNoteModal />}
      {state.showBenefitEnrollmentModal && (
        <BenefitEnrollmentFlowContainer enrollmentType={state.enrollmentType} onExit={onEnrollmentExit} />
      )}
      {state.showPersonalEmailVerifiedModal && <EmailVerifiedModal />}
    </ScrollToTop>
  )
}

export default Home
